import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import Depth0Row from './TableRow/Depth0Row';
import Depth1Row from './TableRow/Depth1Row';
import Depth2Row from './TableRow/Depth2Row';
import { REPORT_RESULT_WITH } from '../../_constants/styleConst';

type ResultTableProps = {
  tableData: CategoryResultTable[];
  isPreview: boolean;
};

export type ItemResultTable = {
  itemId: string;
  label: string;
  inResult: string;
  qcResult: string;
  inNumericalValue?: number | null;
  qcNumericalValue?: number | null;
  itemResultList: ItemResultTable[];
};

export type TypeResultTable = {
  typeId: string;
  label: string;
  itemResultList: ItemResultTable[];
};

export type CategoryResultTable = {
  categoryId: string;
  label: string;
  typeResultList: TypeResultTable[];
};

export type style = {
  header: {
    textAlign: string;
  };
  category: {
    padding: string;
    width: string;
  };
  type: {
    padding: string;
    width: string;
  };
  item: {
    padding: string;
    border: string;
  };
  result: {
    width: string;
    padding: string;
    textAlign: string;
  };
};

const commonStyles = {
  padding: '0',
  border: 'solid 1px gray',
  fontSize: '18px',
  wordBreak: 'break-word',
};

/** 小項目の階層を深掘りながら行数を数える */
export const calcItemRows = (itemResultList: ItemResultTable[]): number => {
  return itemResultList.reduce((rows, item) => {
    if (item.itemResultList.length === 0) {
      return rows + 1;
    }
    return rows + calcItemRows(item.itemResultList);
  }, 0);
};

const ResultTable: React.FC<ResultTableProps> = ({ tableData, isPreview }) => {
  const { t } = useTranslation();

  const style = {
    header: {
      ...commonStyles,
      textAlign: 'center',
    },
    category: {
      ...commonStyles,
      padding: '0 0 0 2px',
      width: '15%',
    },
    type: {
      ...commonStyles,
      padding: '0 0 0 2px',
      width: '20%',
    },
    item: {
      ...commonStyles,
      padding: '0 0 0 2px',
    },
    result: {
      ...commonStyles,
      width:
        (isPreview
          ? REPORT_RESULT_WITH.FORMAT
          : REPORT_RESULT_WITH.INSPECTION
        ).toString() + '%',
      padding: '0 1px',
      textAlign: 'center',
    },
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell sx={style.header}>
            {t('LBL_INSPECTION_CATEGORY')}
          </TableCell>
          <TableCell sx={style.header}>
            {t('LBL_INSPECTION_SUB_CATEGORY')}
          </TableCell>
          <TableCell sx={style.header} colSpan={3}>
            {t('LBL_INSPECTION_ITEM')}
          </TableCell>
          <TableCell
            colSpan={2}
            sx={{
              ...style.header,
              width: `calc(${style.result.width} * 2)`,
            }}
          >
            {t('LBL_REPORT_INSPECTION')}
          </TableCell>
          <TableCell
            colSpan={2}
            sx={{
              ...style.header,
              width: `calc(${style.result.width} * 2)`,
            }}
          >
            {t('LBL_REPORT_QC')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tableData.map((category) =>
          category.typeResultList.map((type, typeIndex) =>
            type.itemResultList.map((item, itemIndex) => {
              // 0層目のみ
              if (item.itemResultList.length === 0) {
                return (
                  <Depth0Row
                    key={`${item.itemId}_${itemIndex}`}
                    category={category}
                    type={type}
                    item={item}
                    typeIndex={typeIndex}
                    itemIndex={itemIndex}
                    tableStyle={style}
                  />
                );
              }
              return item.itemResultList.map((problem, problemIndex) => {
                // 1層目まで
                if (problem.itemResultList.length === 0) {
                  return (
                    <Depth1Row
                      key={`${problem.itemId}_${problemIndex}`}
                      category={category}
                      type={type}
                      item={item}
                      problem={problem}
                      typeIndex={typeIndex}
                      itemIndex={itemIndex}
                      problemIndex={problemIndex}
                      tableStyle={style}
                    />
                  );
                }
                // 2層目まで
                return problem.itemResultList.map((detail, detailIndex) => {
                  return (
                    <Depth2Row
                      key={`${detail.itemId}_${detailIndex}`}
                      category={category}
                      type={type}
                      item={item}
                      problem={problem}
                      detail={detail}
                      typeIndex={typeIndex}
                      itemIndex={itemIndex}
                      problemIndex={problemIndex}
                      detailIndex={detailIndex}
                      tableStyle={style}
                    />
                  );
                });
              });
            }),
          ),
        )}
      </TableBody>
    </Table>
  );
};

export default ResultTable;
